import React, {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	SortingState,
	getSortedRowModel,
	AccessorKeyColumnDef,
} from '@tanstack/react-table'
import { RefObject, useEffect, useMemo, useState } from 'react'
import {
	Delete,
	Done,
	Edit,
	InfoOutlined,
	KeyboardArrowDown,
	KeyboardArrowUp,
	UnfoldMore,
	Visibility,
} from '@mui/icons-material'
import { IconButton, OptionsMenuButton, Tooltip } from '../../components/ui'
import { useUserDateFormat } from '../useUserDateFormat'
import { ReactComponent as ResetColumnsSize } from '../../assets/icons/table/reset-columns.svg'
import cn from '../../lib/utils/cn'
import { shortenFileName } from '../../lib/utils/formatDocument'

interface TableBoardStatusCard extends BoardStatusCard {
	status: Omit<BoardStatus, 'cards'>
}

interface DataTableEntry extends TableBoardStatusCard {
	options: {
		id: number
	}
}

import { useCustomGlobalFilter } from './useColumnFilters'
import {
	BoardStatus,
	BoardStatusCard,
} from '../../lib/api/types/board.interfaces'
import { PriorityLabel } from '../../pages/MySpace/BoardsPages/components/ColumnCard'
import { UserAvatar } from '../../components/modals/mySpace/boards/ViewCardModal'

interface useCreateTodoTableProps {
	data?: TableBoardStatusCard[] | null
	visibleColumns: string[]
	containerRef?: RefObject<HTMLDivElement>
	onDelete: (id: string) => void
	onEdit: (id: string) => void
	onOpen: (id: string) => void
	isMobile?: boolean
	isShrink?: boolean
	isOwner?: boolean
}

const customGlobalFilter = useCustomGlobalFilter()

export const useCreateTodoTable = ({
	data = [],
	visibleColumns,
	containerRef,
	onDelete,
	onEdit,
	onOpen,
	isMobile,
	isShrink,
	isOwner,
}: useCreateTodoTableProps) => {
	const { formatDate, formatDateTime } = useUserDateFormat()
	const [sorting, setSorting] = useState<SortingState>([])
	const renderSortingHeader = (
		title: string,
		column: React.Column<DataTableEntry, unknown>,
		className?: string
	) => {
		return (
			<div
				className={cn(
					'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-6 box-border',
					className
				)}
			>
				<span className="text-center w-fit max-w-auto truncate">{title}</span>
				<span
					className={cn(
						'flex items-center justify-center transition-all duration-200 w-3 h-5',
						column.getIsSorted() === false && 'hidden group-hover:inline-block'
					)}
				>
					{column.getIsSorted() === 'asc' && (
						<KeyboardArrowUp className="w-5 h-5" />
					)}
					{column.getIsSorted() === 'desc' && (
						<KeyboardArrowDown className="w-5 h-5" />
					)}
					{column.getIsSorted() === false && (
						<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
					)}
				</span>
			</div>
		)
	}
	const tableData = useMemo(() => {
		const safeData = Array.isArray(data) ? data : []
		return safeData
			?.map((entry) => ({
				id: entry.id,
				title: entry?.title,
				priority: entry?.priority,
				status: entry?.status,
				assignedTo: entry?.assignedTo,
				createdBy: entry?.createdBy,
				createdAt: entry?.createdAt || null,
				dueDate: entry?.dueDate,
				completedAt: entry?.completedAt,
				options: {
					id: entry?.id,
				},
			}))
			.sort((a, b) => {
				return (
					new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
				)
			})
	}, [data])

	const columns = useMemo<ColumnDef<DataTableEntry>[]>(() => {
		const base: ColumnDef<DataTableEntry>[] = [
			{
				accessorKey: 'title',
				enableResizing: true,
				enableSorting: true,
				minSize: 50,
				size: 160,
				sortingFn: 'alphanumeric',
				header: ({ column }) => {
					return (
						<div className="h-full flex items-center gap-3 max-w-full pl-3 md:pl-6 group dark:text-text-table-dark cursor-pointer">
							{renderSortingHeader('Title', column, 'w-fit pl-0')}
						</div>
					)
				},
				cell: ({ row }) => {
					const name = String(row?.original?.title)

					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis">
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main dark:text-text-table-dark truncate font-medium text-sm">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'status',
				enableResizing: true,
				enableSorting: true,
				minSize: 120,
				size: 120,
				sortingFn: 'alphanumeric',
				header: ({ column }) => (
					<span className="dark:text-text-table-dark">
						{renderSortingHeader('Status', column)}
					</span>
				),
				cell: (info) => {
					const { name, color } = info?.row?.original?.status ?? {}
					return (
						<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
							<div className="flex items-center justify-start gap-2">
								<div
									className="w-0.5 min-w-0.5 h-3.5 rounded-full"
									style={{
										backgroundColor: color ?? '#FF73A6',
									}}
								/>

								<p className="text-text-secondary font-medium text-sm truncate  dark:text-text-table-dark">
									{name ?? ''}
								</p>
							</div>
						</Tooltip>
					)
				},
			},
			{
				accessorKey: 'createdBy',
				enableResizing: true,
				minSize: 130,
				size: 130,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				header: ({ column }) => (
					<span className="dark:text-text-table-dark">
						{renderSortingHeader('Created by', column)}
					</span>
				),
				cell: (info) => {
					const { name, email } = info?.row?.original?.createdBy ?? {}
					return (
						<div className="flex items-center gap-3">
							<UserAvatar
								user={info?.row?.original?.createdBy}
								className="w-8 h-8 min-w-8"
							/>
							<p className="text-text-main font-medium text-sm w-auto dark:text-text-table-dark truncate">
								{name || email}
							</p>
						</div>
					)
				},
			},
			{
				accessorKey: 'assignedTo',
				enableResizing: true,
				minSize: 130,
				size: 130,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				header: ({ column }) => (
					<span className="dark:text-text-table-dark">
						{renderSortingHeader('Assignment', column)}
					</span>
				),
				cell: (info) => {
					const { name, email } = info?.row?.original?.assignedTo ?? {}
					return (
						<div className="flex items-center gap-3">
							<UserAvatar
								user={info?.row?.original?.assignedTo}
								className="w-8 h-8 min-w-8"
							/>
							<Tooltip
								label={name || email}
								className={cn(isMobile && 'opacity-0')}
							>
								<p className="text-text-main dark:text-text-table-dark font-medium text-sm w-auto truncate">
									{name || email}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'priority',
				enableResizing: true,
				minSize: 90,
				size: 90,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				header: ({ column }) => (
					<span className="dark:text-text-table-dark">
						{renderSortingHeader('Priority', column)}
					</span>
				),
				cell: (info) => (
					<PriorityLabel
						priority={Number(info?.getValue()) || null}
						isShrink={isShrink}
					/>
				),
			},
			{
				accessorKey: 'createdAt',
				enableResizing: true,
				enableSorting: true,
				minSize: 100,
				size: 100,
				sortingFn: 'datetime',
				header: ({ column }) => (
					<span className="dark:text-text-table-dark">
						{renderSortingHeader('Created date', column)}
					</span>
				),
				cell: (info) => {
					const val = info.getValue()
					return (
						<span className="font-medium text-text-main dark:text-text-table-dark text-sm">
							{val
								? <Tooltip label={formatDateTime(String(val))} classNameChildrenWrapper="w-fit">
									<span>{formatDate(String(val))}</span>
								</Tooltip>
								: '-'}
						</span>
					)
				},
			},
			{
				accessorKey: 'dueDate',
				enableResizing: true,
				enableSorting: true,
				minSize: 100,
				size: 100,
				sortingFn: 'datetime',
				header: ({ column }) => (
					<span className="dark:text-text-table-dark">
						{renderSortingHeader('Due date', column)}
					</span>
				),
				cell: (info) => {
					const val = info.getValue()
					return (
						<span className="font-medium text-text-main dark:text-text-table-dark text-sm">
							{val ? formatDate(String(val)) : '-'}
						</span>
					)
				},
			},
			{
				accessorKey: 'completedAt',
				enableResizing: true,
				enableSorting: true,
				minSize: 100,
				size: 100,
				sortingFn: 'datetime',
				header: ({ column }) => (
					<span className="dark:text-text-table-dark">
						{renderSortingHeader('Completion date', column)}
					</span>
				),
				cell: (info) => {
					const val = info.getValue()
					return (
						<p className="flex items-center justify-start gap-1 font-medium text-text-main text-sm">
							<Done className="fill-positive w-4 h-4" />
							{val
								? <Tooltip label={formatDateTime(String(val))} classNameChildrenWrapper="w-fit">
									<span>{formatDate(String(val))}</span>
								</Tooltip>
								: '-'}
						</p>
					)
				},
			},
			{
				accessorKey: 'options',
				enableResizing: false,
				enableSorting: false,
				size: isMobile ? 50 : 30,
				maxSize: 50,
				minSize: 25,
				header: () => (
					<div className="flex items-center justify-center gap-1">
						<IconButton
							variant="transparent"
							className="w-8 h-8 group hover:bg-transparent"
							tooltipPosition="left"
							icon={
								<ResetColumnsSize className="fill-text-table w-5 h-5 group-hover:fill-accent-hover" />
							}
							onClick={() => table.resetColumnSizing()}
							tooltip={'Reset columns sizes'}
						/>
					</div>
				),
				cell: ({ row }) => {
					return (
						<div className="bg-inherit md:absolute right-0 flex items-center justify-end gap-2 w-fit md:ml-auto pl-5 h-full group-hover:lg:left-custom-shadow opacity-0 group-hover:opacity-100 duration-default">
							{isOwner ? (
								<OptionsMenuButton
									buttonClassName="ml-auto mr-10"
									parentScrollRef={containerRef}
									items={[
										{
											icon: <Visibility />,
											label: 'Open',
											onClick: () => onOpen(row?.original?.id),
										},
										{
											icon: <Edit />,
											label: 'Edit',
											onClick: () => onEdit(row?.original?.id),
										},
										{
											icon: <Delete />,
											label: 'Delete',
											onClick: () => onDelete(row?.original?.id),
											visible: true,
											withDivider: true,
										},
									]}
								/>
							) : (
								<IconButton
									icon={<InfoOutlined className="w-5 h-5" />}
									className="w-8 h-8 mr-2"
									variant="secondary"
									onClick={() => onOpen(row?.original?.id)}
									tooltip={'Task info'}
								/>
							)}
						</div>
					)
				},
			},
		]

		if (isMobile) {
			return base.filter(
				(columnDef): columnDef is AccessorKeyColumnDef<DataTableEntry, any> => {
					return (
						'accessorKey' in columnDef &&
						['id', 'title', 'options'].includes(columnDef.accessorKey)
					)
				}
			)
		}

		return base
	}, [data, isMobile, isShrink, formatDate, formatDateTime])

	const tableConfig = useMemo(
		() => ({
			data: tableData,
			columns,
			columnResizeMode: 'onChange' as const,
			getCoreRowModel: getCoreRowModel(),
			getPaginationRowModel: getPaginationRowModel(),
			getFilteredRowModel: getFilteredRowModel(),
			getSortedRowModel: getSortedRowModel(),
			globalFilterFn: customGlobalFilter,
			manualSorting: false,
			enableSorting: true,
			enableSortingRemoval: true,
			onSortingChange: setSorting,
			state: {
				sorting,
				// globalFilter: searchQuery || '',
			},
			debugTable: false,
			debugHeaders: false,
			debugColumns: false,
		}),
		[tableData, columns, sorting]
	)

	const table = useReactTable(tableConfig)

	useEffect(() => {
		table.getAllColumns().forEach((column) => {
			table
				.getColumn(column.id)
				?.toggleVisibility(visibleColumns.includes(column.id))
		})
	}, [visibleColumns, table])

	return { table }
}
